// Color utilities: pure functions used across the app

// parse various color token formats to {r,g,b}
export function parseColorToRgb(input) {
  if (!input) return { r: 0, g: 0, b: 0 }
  const s = input.trim()
  // rgb(...) format
  const rgbMatch = s.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/i)
  if (rgbMatch) {
    return { r: parseInt(rgbMatch[1]), g: parseInt(rgbMatch[2]), b: parseInt(rgbMatch[3]) }
  }
  // hex format
  let h = s.replace('#', '')
  if (h.length === 0) return { r: 0, g: 0, b: 0 }
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  if (h.length === 6) {
    const n = parseInt(h, 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }
  // fallback
  return { r: 0, g: 0, b: 0 }
}

// RGB -> HSL
export function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0, l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
    }
    h /= 6
  }
  return { h: h * 360, s: s * 100, l: l * 100 }
}

// HSL -> hex
export function hslToHex(h, s, l) {
  s /= 100; l /= 100
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2
  let r = 0, g = 0, b = 0
  if (0 <= h && h < 60) { r = c; g = x; b = 0 }
  else if (60 <= h && h < 120) { r = x; g = c; b = 0 }
  else if (120 <= h && h < 180) { r = 0; g = c; b = x }
  else if (180 <= h && h < 240) { r = 0; g = x; b = c }
  else if (240 <= h && h < 300) { r = x; g = 0; b = c }
  else { r = c; g = 0; b = x }
  const toHex = v => ('0' + Math.round((v + m) * 255).toString(16)).slice(-2)
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// Adjust lightness by percentage points (HSL L axis)
export function adjustLightness(hex, deltaPercent) {
  const rgb = parseColorToRgb(hex)
  const hsl = rgbToHsl(rgb)
  const newL = Math.max(0, Math.min(100, hsl.l + deltaPercent))
  return hslToHex(hsl.h, hsl.s, newL)
}

// Compute contrast color (black or white) using relative luminance
export function getContrastColor(hex) {
  const { r, g, b } = parseColorToRgb(hex)
  const toLin = v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b)
  return L > 0.179 ? '#000000' : '#FFFFFF'
}
